/**
 * "Drawn for left" variants of asymmetric art (lane C0-8DIR, true eight directions; rig/dir8.ts).
 *
 * A left-facing view is drawn as its right-facing view of the person's mirror image, then flipped (dir8.ts). For a
 * symmetric part that changes nothing; an asymmetric one — a pin through one side of the bun, a flower behind one
 * ear, a scar on one cheek, a taiji, a sash over one shoulder — must be drawn as the mirror image's part, or the flip
 * puts it on the wrong side (or prints it back to front). Each such asset says how, beside its art:
 * - head-on definitions (mouths) are simply mirrored (`mirrorRows`);
 * - prints and things placed by hand across the body flip their screen-x when `printDir` / `lateral` say so;
 * - per-view pixel grids carry a `LeftArt`: views whose grid is the mirror image of its own (`flip`, about the
 *   grid's own anchor: right for small parts on the head's middle line seen three-quarter on), views where the part
 *   is out of sight (`hide`), or grids drawn by hand (`grids`). A view not named keeps its grid.
 */
import type { View } from '../sprites';
import type { Grid } from './hair';
import type { Skeleton } from './skeleton';

export interface LeftArt {
  flip?: readonly View[];
  hide?: readonly View[];
  grids?: Partial<Record<View, Grid>>;
}

/** A grid mirrored about its anchor cell (`ox`). */
export function flipGrid(g: Grid): Grid {
  const w = Math.max(...g.rows.map((r) => r.length));
  return { ox: w - 1 - g.ox, oy: g.oy, rows: g.rows.map((r) => [...r.padEnd(w, '.')].reverse().join('')) };
}

const FLIPPED = new WeakMap<Grid, Grid>();

/** The grid to draw for this frame: the asset's own, or on a left view its `LeftArt` variant (undefined: not seen). */
export function gridFor(own: Grid | undefined, sk: Pick<Skeleton, 'view' | 'left'>, left?: LeftArt): Grid | undefined {
  if (!sk.left || !left) return own;
  if (left.hide?.includes(sk.view)) return undefined;
  const drawn = left.grids?.[sk.view];
  if (drawn) return drawn;
  if (own && left.flip?.includes(sk.view)) {
    let f = FLIPPED.get(own);
    if (!f) FLIPPED.set(own, (f = flipGrid(own)));
    return f;
  }
  return own;
}

/** Rows mirrored left to right (a head-on definition, for the mirror image). */
export const mirrorRows = (rows: readonly string[]): string[] => rows.map((r) => [...r].reverse().join(''));

/** Which way a print (an emblem, embroidery) runs across the screen: -1 on a left view, so the flip reads true. */
export const printDir = (sk: Pick<Skeleton, 'left'>): 1 | -1 => (sk.left ? -1 : 1);

/**
 * Which way a feature placed by hand on one side of the body lies across the screen, against the right-facing
 * view: -1 on a left view seen three-quarter on (the body's sides swap places there); in profile only depth swaps.
 */
export const lateral = (sk: Pick<Skeleton, 'left' | 'view'>): 1 | -1 =>
  sk.left && (sk.view === 'front3' || sk.view === 'back3') ? -1 : 1;
